import SectionEyebrow from "./SectionEyebrow";
import { Fragment } from "react";
import { Link } from "@/lib/router-compat";
import { motion } from "framer-motion";
import { ArrowRight, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import type { ServiceData } from "@/data/servicesData";
import { useLanguage } from "@/hooks/useLanguage";
import { localizeHref } from "@/lib/localizeHref";
import { getServiceDetailT } from "@/i18n/serviceDetail";
import { cn } from "@/lib/utils";
import type { LucideIcon } from "lucide-react";

interface Props {
  service: ServiceData;
  onCtaClick: () => void;
  parentCrumbs?: {label: string;to: string;}[];
  baseCrumb?: {label: string;to: string;};
  partnerLogo?: { src: string; alt: string } | { src: string; alt: string }[];
  hideDiscoverButton?: boolean;
  discoverButtonTarget?: string;
  discoverButtonLabel?: string;
  discoverButtonIcon?: LucideIcon;
  secondaryCta?: { label: string; href: string };
  tertiaryCta?: { label: string; href: string; className?: string; icon?: LucideIcon };
  primaryCtaLabel?: string;
  /** Optional size override for the subtitle line; defaults to the standard size. */
  subtitleClassName?: string;
  /** Optional alt text override for the hero image; defaults to "title – subtitle". */
  heroImageAlt?: string;
  /** Opt-in eyebrow above the H1 (unified QA/Toxicology style, light variant). */
  eyebrow?: string;
  eyebrowAccent?: string;
}

const isLightColor = (hex: string) => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return (r * 299 + g * 587 + b * 114) / 1000 > 150;
};

const ServiceHero = ({ service, onCtaClick, parentCrumbs, baseCrumb, partnerLogo, hideDiscoverButton, discoverButtonTarget, discoverButtonLabel, discoverButtonIcon: DiscoverIcon = ChevronDown, secondaryCta, tertiaryCta, primaryCtaLabel, subtitleClassName, heroImageAlt, eyebrow, eyebrowAccent }: Props) => {
  const sc = service.color;
  const lang = useLanguage();
  const t = getServiceDetailT(lang);
  const base = baseCrumb || { label: t.services, to: localizeHref("/en/services", lang) };

  return (
    <section className="relative text-primary-foreground pt-44 pb-16 sm:pb-20 overflow-hidden">
      <div className="absolute inset-0">
        <img
          src={service.heroImage}
          alt={heroImageAlt ?? `${service.title} – ${service.subtitle}`}
          className="w-full h-full object-cover"
          fetchPriority="high"
          loading="eager" />

        <div className="absolute inset-0 bg-gradient-to-r from-primary/90 via-primary/75 to-primary/50" />
      </div>

      {/* Decorative service-colored accent bar */}
      <div
        className="absolute bottom-0 left-0 right-0 h-1"
        style={{ background: `linear-gradient(90deg, ${sc}, transparent)` }} />


      <div className="relative z-10 container mx-auto px-6 lg:px-10 max-w-4xl">
        <nav aria-label="Breadcrumb" className="mb-8">
          <ol className="flex items-center gap-2 text-xs text-primary-foreground/40 tracking-wide uppercase">
            <li><Link to={localizeHref("/", lang)} className="hover:text-primary-foreground/70 transition-colors">{t.home}</Link></li>
            <li aria-hidden="true" className="text-primary-foreground/20">/</li>
            <li><Link to={base.to} className="hover:text-primary-foreground/70 transition-colors">{base.label}</Link></li>
            {parentCrumbs?.map((crumb) => (
              <Fragment key={crumb.to}>
                <li aria-hidden="true" className="text-primary-foreground/20">/</li>
                <li><Link to={crumb.to} className="hover:text-primary-foreground/70 transition-colors">{crumb.label}</Link></li>
              </Fragment>
            ))}
            <li aria-hidden="true" className="text-primary-foreground/20">/</li>
            <li className="text-primary-foreground/60 truncate max-w-[200px]">{service.title}</li>
          </ol>
        </nav>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}>
          {eyebrow && <SectionEyebrow light accent={eyebrowAccent}>{eyebrow}</SectionEyebrow>}
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mb-4">
            {service.title}
          </h1>
          {/* Answer-first block: this intro is the page's quotable summary and the
              `speakable` / `abstract` target for answer engines. */}
          <div className="key-takeaways">
            <p className={cn("text-lg text-primary-foreground/70 max-w-2xl mb-4", subtitleClassName)}>
              {service.subtitle}
            </p>
            {service.description && (
              <div className="max-w-2xl mb-8 space-y-3">
                {service.description.split(/\n\n+/).map((para, i) => (
                  <p key={i} className="text-base text-primary-foreground/55 leading-relaxed">
                    {para}
                  </p>
                ))}
              </div>
            )}
          </div>
          <div className="flex flex-wrap gap-3">
            {!hideDiscoverButton && (
              <Button
                size="lg"
                variant="outline"
                className="rounded-full px-8 border-primary-foreground/20 text-primary-foreground hover:bg-primary-foreground/10 bg-transparent"
                onClick={() => {
                  if (discoverButtonTarget) {
                    document.getElementById(discoverButtonTarget)?.scrollIntoView({ behavior: "smooth" });
                  } else {
                    (document.getElementById("therapeutic-areas") || document.getElementById("industry-solutions") || document.getElementById("service-offerings"))?.scrollIntoView({ behavior: "smooth" });
                  }
                }}>
                {discoverButtonLabel || t.discoverSolutions} <DiscoverIcon size={16} className="ml-2" />
              </Button>
            )}
            {secondaryCta && (
              <a href={secondaryCta.href}>
                <Button
                  size="lg"
                  variant="outline"
                  className="rounded-full px-8 border-primary-foreground/20 text-primary-foreground hover:bg-primary-foreground/10 bg-transparent"
                >
                  {secondaryCta.label} <ChevronDown size={16} className="ml-2" />
                </Button>
              </a>
            )}
            <Button
              size="lg"
              className="rounded-full px-8 shadow-lg hover:opacity-90 bg-white text-primary hover:bg-white/90"
              onClick={onCtaClick}>
              {primaryCtaLabel || t.getInTouch} <ArrowRight size={16} className="ml-2" />
            </Button>
            {tertiaryCta && (
              <a href={tertiaryCta.href}>
                <Button
                  size="lg"
                  className={cn(
                    "rounded-full px-8 shadow-lg hover:opacity-90 gap-2",
                    tertiaryCta.className
                  )}
                >
                  {tertiaryCta.icon && <tertiaryCta.icon size={16} />}
                  {tertiaryCta.label} <ArrowRight size={16} />
                </Button>
              </a>
            )}
          </div>

        </motion.div>
      </div>
    </section>);

};

export default ServiceHero;